import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { IUserDetails } from "../@types/Login";
import { CombinedMeasurementsConfig } from "../@types/Measurements";
import {
  IPatientDataChangedNotification,
  PatientDataChangeType,
} from "../@types/SignalR";
import { queryClient } from "../config/query";
import { AUTH_USER_DETAILS } from "../constants/localStorageKeys";
import { useSignalR } from "../hooks/useSignalR";

interface IPatientPageNotificationContextType {
  isConnected: boolean;
  lastNotification: IPatientDataChangedNotification | null;
  hasNewData: boolean;
  reconnect: () => Promise<void>;
  dismissNewData: () => void;
  refreshPage: () => void;
}

interface IPatientPageNotificationProviderProps {
  children: React.ReactNode;
  patientId?: string;
}

const PatientPageNotificationContext =
  createContext<IPatientPageNotificationContextType>(
    {} as IPatientPageNotificationContextType
  );

const getCurrentUserId = (): number | null => {
  try {
    const userDetailsStr = localStorage.getItem(AUTH_USER_DETAILS);
    if (!userDetailsStr) return null;
    const userDetails: IUserDetails = JSON.parse(userDetailsStr);
    return userDetails.userId ?? null;
  } catch {
    return null;
  }
};

const getQueryKeysForDataType = (
  dataType: PatientDataChangeType,
  patientId: string
): string[][] => {
  switch (dataType) {
    case "Measurement":
      return [
        [`patient-measurements-${patientId}`],
        [`patient-info-${patientId}`],
        [`patient-monitoring-info-${patientId}`],
      ];
    case "Survey":
      return [[`patient-surveys-${patientId}`], [`patient-proms-${patientId}`]];
    case "Note":
      return [[`patient-notes-${patientId}`]];
    case "Alert":
      return [[`patient-alerts-${patientId}`], [`patient-info-${patientId}`]];
    case "Appointment":
      return [[`patient-appointments-${patientId}`]];
    case "Message":
      return [[`patient-messages-${patientId}`]];
    case "PatientInfo":
      return [[`patient-info-${patientId}`]];
    case "Form":
      return [[`patient-forms-${patientId}`]];
    default:
      return [];
  }
};

// EN-5060: whether the patient page renders a card for this measurement type — from the cached
// combined configs (the same response the page draws its cards from). With no cached card set
// (page still loading, cache evicted) it errs on announcing rather than silently dropping a
// possibly-visible change.
const isMeasurementCardVisible = (
  patientId: string,
  measurementType: string | undefined
): boolean => {
  if (!measurementType) return true;

  const configs = queryClient.getQueryData<CombinedMeasurementsConfig | null>([
    "combined-measurement-configs",
    Number(patientId),
  ]);

  if (!configs) return true;

  return [...(configs.measurements ?? []), ...(configs.labResults ?? [])].some(
    (config) => config.type === measurementType
  );
};

export function PatientPageNotificationProvider({
  children,
  patientId,
}: IPatientPageNotificationProviderProps) {
  const [lastNotification, setLastNotification] =
    useState<IPatientDataChangedNotification | null>(null);
  const [hasNewData, setHasNewData] = useState(false);

  const dismissNewData = useCallback(() => {
    setHasNewData(false);
  }, []);

  const refreshPage = useCallback(() => {
    setHasNewData(false);
    window.location.reload();
  }, []);

  const handleDataChanged = useCallback(
    (notification: IPatientDataChangedNotification) => {
      // Skip notifications for changes made by the current user (self-exclusion)
      const currentUserId = getCurrentUserId();
      if (
        currentUserId !== null &&
        notification.changedByUserId === currentUserId
      ) {
        return;
      }

      // EN-5060: a measurement type hidden on portal renders no card, so announcing it would
      // invite a refresh that shows nothing. Only the popup is suppressed — queries below are
      // still invalidated.
      const announces =
        notification.dataType !== "Measurement" ||
        !patientId ||
        isMeasurementCardVisible(patientId, notification.measurementType);

      setLastNotification(notification);
      if (announces) {
        setHasNewData(true);
      }

      // Invalidate queries so data is fresh when user refreshes
      if (patientId) {
        const queryKeys = getQueryKeysForDataType(
          notification.dataType as PatientDataChangeType,
          patientId
        );

        queryKeys.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }
    },
    [patientId]
  );

  const { isConnected, reconnect } = useSignalR({
    patientId,
    onDataChanged: handleDataChanged,
    enabled: !!patientId,
  });

  const value = useMemo(
    () => ({
      isConnected,
      lastNotification,
      hasNewData,
      reconnect,
      dismissNewData,
      refreshPage,
    }),
    [
      isConnected,
      lastNotification,
      hasNewData,
      reconnect,
      dismissNewData,
      refreshPage,
    ]
  );

  return (
    <PatientPageNotificationContext.Provider value={value}>
      {children}
    </PatientPageNotificationContext.Provider>
  );
}

export function usePatientPageNotification() {
  const context = useContext(PatientPageNotificationContext);
  return context;
}
